import logoPos from "../../../assets/logos/hulahoop-logo-pos.svg";
import { DividerDashed } from "./DividerDashed";

interface FooterLink {
	label: string;
	href: string;
	external?: boolean;
}

const footerLinks: Record<string, FooterLink[]> = {
	producto: [
		{ label: "Gestión", href: "#gestiona" },
		{ label: "Automatización", href: "#automatiza" },
		{ label: "Control", href: "#controla" },
		{ label: "Hub Inversor", href: "#hub" },
	],
	recursos: [
		{ label: "Blog", href: "#" },
		{ label: "Documentación", href: "#" },
		{ label: "Changelog", href: "#" },
	],
	empresa: [
		{ label: "Nosotros", href: "https://hulahoop.media/", external: true },
		{ label: "Contacto", href: "/contact" },
	],
	legal: [
		{ label: "Aviso legal", href: "/legal-advice" },
		{ label: "Privacidad", href: "/privacy-policy" },
		{ label: "Cookies", href: "/cookies-policy" },
	],
};

// Social icons
function LinkedInIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 382 382" aria-hidden="true" className={className}>
			<path
				fill="currentColor"
				d="M347.445,0H34.555C15.471,0,0,15.471,0,34.555v312.889C0,366.529,15.471,382,34.555,382h312.889C366.529,382,382,366.529,382,347.444V34.555C382,15.471,366.529,0,347.445,0z M118.207,329.844c0,5.554-4.502,10.056-10.056,10.056H65.345c-5.554,0-10.056-4.502-10.056-10.056V150.403c0-5.554,4.502-10.056,10.056-10.056h42.806c5.554,0,10.056,4.502,10.056,10.056V329.844z M86.748,123.432c-22.459,0-40.666-18.207-40.666-40.666S64.289,42.1,86.748,42.1s40.666,18.207,40.666,40.666S109.208,123.432,86.748,123.432z M341.91,330.654c0,5.106-4.14,9.246-9.246,9.246H286.73c-5.106,0-9.246-4.14-9.246-9.246v-84.168c0-12.556,3.683-55.021-32.813-55.021c-28.309,0-34.051,29.066-35.204,42.11v97.079c0,5.106-4.139,9.246-9.246,9.246h-44.426c-5.106,0-9.246-4.14-9.246-9.246V149.593c0-5.106,4.14-9.246,9.246-9.246h44.426c5.106,0,9.246,4.14,9.246,9.246v15.655c10.497-15.753,26.097-27.912,59.312-27.912c73.552,0,73.131,68.716,73.131,106.472L341.91,330.654L341.91,330.654z"
			/>
		</svg>
	);
}

function InstagramIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 551.034 551.034" aria-hidden="true" className={className}>
			<path
				fill="currentColor"
				d="M386.878,0H164.156C73.64,0,0,73.64,0,164.156v222.722c0,90.516,73.64,164.156,164.156,164.156h222.722c90.516,0,164.156-73.64,164.156-164.156V164.156C551.033,73.64,477.393,0,386.878,0z M495.6,386.878c0,60.045-48.677,108.722-108.722,108.722H164.156c-60.045,0-108.722-48.677-108.722-108.722V164.156c0-60.046,48.677-108.722,108.722-108.722h222.722c60.045,0,108.722,48.676,108.722,108.722L495.6,386.878L495.6,386.878z"
			/>
			<path
				fill="currentColor"
				d="M275.517,133C196.933,133,133,196.933,133,275.516s63.933,142.517,142.517,142.517S418.034,354.1,418.034,275.516S354.101,133,275.517,133z M275.517,362.6c-48.095,0-87.083-38.988-87.083-87.083s38.989-87.083,87.083-87.083c48.095,0,87.083,38.988,87.083,87.083C362.6,323.611,323.611,362.6,275.517,362.6z"
			/>
			<circle fill="currentColor" cx="418.306" cy="134.072" r="34.149" />
		</svg>
	);
}

function FooterLinkItem({ label, href, external }: FooterLink) {
	const externalProps = external
		? { target: "_blank", rel: "noopener noreferrer" }
		: {};
	return (
		<li>
			<a
				href={href}
				{...externalProps}
				className="text-xs font-semibold text-text-tertiary hover:text-text-secondary transition-colors"
			>
				{label}
			</a>
		</li>
	);
}

function MediumIcon({ className }: { className?: string }) {
	return (
		<svg viewBox="0 0 24 24" aria-hidden="true" className={className}>
			<path
				fill="currentColor"
				d="M13 12C13 15.3137 10.3137 18 7 18C3.68629 18 1 15.3137 1 12C1 8.68629 3.68629 6 7 6C10.3137 6 13 8.68629 13 12Z"
			/>
			<path
				fill="currentColor"
				d="M23 12C23 14.7614 22.5523 17 22 17C21.4477 17 21 14.7614 21 12C21 9.23858 21.4477 7 22 7C22.5523 7 23 9.23858 23 12Z"
			/>
			<path
				fill="currentColor"
				d="M17 18C18.6569 18 20 15.3137 20 12C20 8.68629 18.6569 6 17 6C15.3431 6 14 8.68629 14 12C14 15.3137 15.3431 18 17 18Z"
			/>
		</svg>
	);
}

/**
 * Footer - Clerk-style compact layout.
 * No background to let grid lines continue through.
 * Custom responsive padding: 48px / 56px / 64px
 */
export function Footer() {
	return (
		<footer className="relative z-10">
			{/* Top dashed line */}
			<DividerDashed intensity="hard" />

			{/* Grid sin gap - igual que GridLines para alineación perfecta */}
			<div className="w-full max-w-[1440px] mx-auto grid grid-cols-4 px-6 md:grid-cols-8 md:px-16 xl:grid-cols-12 xl:px-[180px] pt-4 pb-12 md:pb-14 xl:pb-16">
				{/* Logo - col 1 */}
				<div className="col-span-4 pl-5 mb-6 md:col-span-1 md:pl-5 md:mb-0">
					<img src={logoPos} alt="Hulahoop" className="h-6 w-auto" />
				</div>

				{/* Producto - col 6 */}
				<div className="col-span-2 md:col-span-1 col-start-1 md:col-start-4 xl:col-start-6 pl-6 md:pl-0">
					<span className="text-xs text-text-tertiary">Producto</span>
					<ul className="mt-3 space-y-2">
						{footerLinks.producto.map((link) => (
							<FooterLinkItem key={link.label} {...link} />
						))}
					</ul>
				</div>

				{/* Recursos - col 8 */}
				<div className="col-span-2 md:col-span-1 col-start-3 md:col-start-6 xl:col-start-8 pl-6 md:pl-0">
					<span className="text-xs text-text-tertiary">Recursos</span>
					<ul className="mt-3 space-y-2">
						{footerLinks.recursos.map((link) => (
							<FooterLinkItem key={link.label} {...link} />
						))}
					</ul>
				</div>

				{/* Empresa - col 10 */}
				<div className="col-span-2 md:col-span-1 col-start-1 md:col-start-8 xl:col-start-10 mt-8 md:mt-0 pl-6 md:pl-0">
					<span className="text-xs text-text-tertiary">Empresa</span>
					<ul className="mt-3 space-y-2">
						{footerLinks.empresa.map((link) => (
							<FooterLinkItem key={link.label} {...link} />
						))}
					</ul>
				</div>

				{/* Legal - col 12 */}
				<div className="col-span-2 md:col-span-1 col-start-3 md:hidden xl:block xl:col-start-12 mt-8 md:mt-0 pl-6 md:pl-0">
					<span className="text-xs text-text-tertiary">Legal</span>
					<ul className="mt-3 space-y-2">
						{footerLinks.legal.map((link) => (
							<FooterLinkItem key={link.label} {...link} />
						))}
					</ul>
				</div>

				{/* Bottom bar - full width */}
				<div className="col-span-4 md:col-span-8 xl:col-span-12 mt-4">
					<DividerDashed intensity="hard" />
				</div>
				<div className="col-span-4 md:col-span-8 xl:col-span-12 pt-4 px-5 flex flex-col md:flex-row items-center justify-between gap-4">
					<p className="text-xs text-text-secondary">© 2026 Hulahoop, S.L.</p>

					{/* Social Icons */}
					<ul className="flex items-center gap-5">
						<li>
							<a
								href="https://www.linkedin.com/company/hulahoop-media/"
								target="_blank"
								rel="noopener noreferrer"
								aria-label="LinkedIn"
								className="block text-text-secondary hover:text-text-tertiary transition-colors"
							>
								<LinkedInIcon className="h-4 w-4" />
							</a>
						</li>
						<li>
							<a
								href="https://www.instagram.com/hulahoop.media/"
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Instagram"
								className="block text-text-secondary hover:text-text-tertiary transition-colors"
							>
								<InstagramIcon className="h-4 w-4" />
							</a>
						</li>
						<li>
							<a
								href="https://medium.com/hulahoop-journal"
								target="_blank"
								rel="noopener noreferrer"
								aria-label="Medium"
								className="block text-text-secondary hover:text-text-tertiary transition-colors"
							>
								<MediumIcon className="h-4 w-4" />
							</a>
						</li>
					</ul>
				</div>
			</div>
		</footer>
	);
}
